/**
 * Web Notification Service
 * Handles web push notifications and in-app notifications
 *
 * Features:
 * - Web Push API (works on Android Chrome, desktop browsers)
 * - Graceful fallback for iOS Safari (in-app only)
 * - Service Worker integration
 * - Permission management
 */

import { Platform } from 'react-native';
import Constants from 'expo-constants';

// Store currently viewing group ID (set by GroupChatScreen)
let currentlyViewingGroupId: string | null = null;

export const setCurrentlyViewingGroup = (groupId: string | null) => {
  currentlyViewingGroupId = groupId;
  console.log(`📍 [Notifications:Web] Currently viewing group:`, groupId);
};

export const getCurrentlyViewingGroupId = async (): Promise<string | null> => {
  return currentlyViewingGroupId;
};

/**
 * Check if Web Push is supported
 */
const isWebPushSupported = (): boolean => {
  return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
};

/**
 * Check if running on iOS (Safari)
 */
const isIOS = (): boolean => {
  return /iPhone|iPad|iPod/.test(navigator.userAgent);
};

/**
 * Request notification permissions from user
 * @returns true if granted, false if denied
 */
export const requestPermissions = async (): Promise<boolean> => {
  console.log(`🔔 [Notifications:Web] Requesting permissions...`);

  if (!isWebPushSupported()) {
    console.warn(`⚠️ [Notifications:Web] Web Push not supported on this browser`);

    // On iOS, show explanation
    if (isIOS()) {
      console.warn(
        `⚠️ [Notifications:Web] iOS Safari doesn't support Web Push. ` +
          `Notifications will only work while the app is open.`
      );
    }

    return false;
  }

  try {
    const permission = await Notification.requestPermission();

    if (permission === 'granted') {
      console.log(`✅ [Notifications:Web] Permission granted`);
      return true;
    } else {
      console.warn(`❌ [Notifications:Web] Permission ${permission}`);
      return false;
    }
  } catch (error) {
    console.error(`❌ [Notifications:Web] Error requesting permissions:`, error);
    return false;
  }
};

/**
 * Get web push subscription
 * This replaces Expo push token on web
 * @returns Push subscription object or null
 */
export const getExpoPushToken = async (): Promise<string | null> => {
  console.log(`📱 [Notifications:Web] Getting web push subscription...`);

  if (!isWebPushSupported()) {
    console.warn(`⚠️ [Notifications:Web] Web Push not supported`);
    return null;
  }

  try {
    // Must have permissions first
    const hasPermission = await requestPermissions();
    if (!hasPermission) {
      return null;
    }

    // Get service worker registration
    const registration = await navigator.serviceWorker.ready;

    // Check for existing subscription
    let subscription = await registration.pushManager.getSubscription();

    // Create new subscription if none exists
    if (!subscription) {
      // Get VAPID public key from app config
      const vapidPublicKey = Constants.expoConfig?.extra?.vapidPublicKey || '';

      if (!vapidPublicKey) {
        console.warn(
          `⚠️ [Notifications:Web] VAPID public key not configured. ` +
            `Web push notifications will be disabled. Check app.config.js`
        );
        return null;
      }

      console.log(`🔑 [Notifications:Web] Using VAPID public key from config`);

      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidPublicKey,
      });
    }

    // Convert subscription to string format
    const subscriptionJSON = JSON.stringify(subscription.toJSON());
    console.log(`✅ [Notifications:Web] Got push subscription`);

    return subscriptionJSON;
  } catch (error) {
    console.error(`❌ [Notifications:Web] Error getting push subscription:`, error);
    return null;
  }
};

/**
 * Configure notification channels (Android only)
 * This is a no-op on web - kept for API compatibility
 */
export const configureNotificationChannels = async (): Promise<void> => {
  // Web doesn't have notification channels concept
  console.log(`📢 [Notifications:Web] Notification channels not applicable on web`);
};

/**
 * Schedule a local notification
 * Uses Web Notification API
 */
export const scheduleTestNotification = async (
  title: string,
  body: string,
  data?: Record<string, any>
): Promise<string> => {
  console.log(`🔔 [Notifications:Web] Scheduling test notification`);

  if (!isWebPushSupported()) {
    throw new Error('Notifications not supported');
  }

  const permission = Notification.permission;

  if (permission !== 'granted') {
    throw new Error('Notification permission not granted');
  }

  // Create notification
  const notification = new Notification(title, {
    body,
    icon: '/icon-192x192.png',
    badge: '/icon-192x192.png',
    tag: data?.id || Date.now().toString(),
    data,
    requireInteraction: false,
  });

  // Handle notification click
  notification.onclick = event => {
    event.preventDefault();
    window.focus();

    // Handle navigation based on data
    if (data?.groupId) {
      // Navigate to group chat
      window.location.href = `/groups/${data.groupId}`;
    }

    notification.close();
  };

  return notification.tag;
};

/**
 * Show in-app notification
 * Fallback for browsers without Web Push support
 */
export const showInAppNotification = (
  title: string,
  body: string,
  data?: Record<string, any>
): void => {
  console.log(`🔔 [Notifications:Web] Showing in-app notification:`, title);

  // Check if we should suppress notification (user viewing that group)
  if (data?.groupId === currentlyViewingGroupId) {
    console.log(`🔕 [Notifications:Web] Suppressing notification - user viewing group`);
    return;
  }

  // Dispatch custom event for app to handle
  window.dispatchEvent(
    new CustomEvent('inAppNotification', {
      detail: { title, body, data },
    })
  );
};

/**
 * Get current badge count
 * Note: Web doesn't support badge API widely yet
 */
export const getBadgeCount = async (): Promise<number> => {
  // Not supported on web
  return 0;
};

/**
 * Set badge count
 * Note: Badge API is experimental on web
 */
export const setBadgeCount = async (count: number): Promise<void> => {
  console.log(`🔢 [Notifications:Web] Setting badge count:`, count);

  // Try experimental Badge API if available
  if ('setAppBadge' in navigator) {
    try {
      if (count > 0) {
        await (navigator as any).setAppBadge(count);
      } else {
        await (navigator as any).clearAppBadge();
      }
    } catch (error) {
      console.warn(`⚠️ [Notifications:Web] Badge API not supported:`, error);
    }
  }
};

/**
 * Clear all notifications
 */
export const clearAllNotifications = async (): Promise<void> => {
  console.log(`🧹 [Notifications:Web] Clearing all notifications`);

  // Can't programmatically close notifications shown by service worker
  // But we can clear badge
  if ('clearAppBadge' in navigator) {
    try {
      await (navigator as any).clearAppBadge();
    } catch (error) {
      console.warn(`⚠️ [Notifications:Web] Error clearing badge:`, error);
    }
  }
};

/**
 * Get device info for storing with push token
 */
export const getDeviceInfo = async () => {
  return {
    device_id: navigator.userAgent,
    device_name: navigator.userAgent.split(' ').slice(-2).join(' ') || 'Web Browser',
    device_os: Platform.OS,
    app_version: '1.0.0',
  };
};

/**
 * Initialize notification system
 * Call this on app startup
 */
export const initializeNotifications = async (): Promise<boolean> => {
  console.log(`🚀 [Notifications:Web] Initializing notification system...`);

  // Check support
  if (!isWebPushSupported()) {
    if (isIOS()) {
      console.log(`⏩ [Notifications:Web] iOS detected - using in-app notifications only`);
    } else {
      console.log(`⏩ [Notifications:Web] Web Push not supported - notifications disabled`);
    }
    return false;
  }

  // Check existing permission
  const existingPermission = Notification.permission;

  if (existingPermission === 'granted') {
    console.log(`✅ [Notifications:Web] Notifications already permitted`);
    return true;
  } else if (existingPermission === 'denied') {
    console.log(`⏩ [Notifications:Web] Notifications denied - skipping initialization`);
    return false;
  }

  // Auto-request permission on login (first time only, browser remembers choice)
  console.log(`🔔 [Notifications:Web] Requesting notification permission...`);

  try {
    const permission = await Notification.requestPermission();

    if (permission === 'granted') {
      console.log(
        `✅ [Notifications:Web] Permission granted! User can now receive push notifications`
      );
      return true;
    } else if (permission === 'denied') {
      console.log(`⏩ [Notifications:Web] Permission denied by user`);
      return false;
    } else {
      // 'default' - user dismissed the prompt
      console.log(`⏩ [Notifications:Web] Permission prompt dismissed`);
      return false;
    }
  } catch (error) {
    console.error(`❌ [Notifications:Web] Error requesting permission:`, error);
    return false;
  }
};

export default {
  requestPermissions,
  getExpoPushToken,
  configureNotificationChannels,
  scheduleTestNotification,
  showInAppNotification,
  getBadgeCount,
  setBadgeCount,
  clearAllNotifications,
  getDeviceInfo,
  initializeNotifications,
  setCurrentlyViewingGroup,
  getCurrentlyViewingGroupId,
  isWebPushSupported,
  isIOS,
};
